// Utilities extracted from MyCalendarModule.js

import _ from "lodash";
import ACLStore from "lib/aclStore";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import {
  LinkSimpleOutlined,
  MicrosoftCalendarColored,
  CopyOutlined,
  CalendarScheduleWeekOutlined,
  GoogleCalendarColored,
} from "@toddle-edu/ds-icons";
import React from "react";
import { MICROSOFT_CALENDAR, GOOGLE_CALENDAR } from "./MyCalendarConstants";

export const isGcalSubscriptionTempfixEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableGcalSubscriptionTempfix");
};

export const isMasterCalendarV1Enabled = ({
  currentCurriculumProgramType = "",
}) => {
  switch (currentCurriculumProgramType) {
    case CURRICULUM_TYPE_DP:
      return ACLStore.can("FeatureFlag:DpMasterCalendarV1");
    default:
      return ACLStore.can("FeatureFlag:MasterCalendarV1");
  }
};

export const isMasterCalendarV2Enabled = () => {
  return ACLStore.can("FeatureFlag:MasterCalendarV2");
};

export const getCourseProjectGroups = ({ courseId, courseGradeFilters }) => {
  if (!courseId) {
    return [];
  }
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });
  const projectGroups = _.get(courseDetails, "projectGroups", []);

  return _.filter(projectGroups, project => !_.isEmpty(project));
};

export const synchronizationDetailsArray = [
  "calendar:sync_note_1",
  "calendar:synchronization_detail_2",
  "calendar:synchronization_detail_3",
  "calendar:sync_note_4",
];

const getLinkIcon = (isCopy = false) => {
  return isCopy ? (
    <CopyOutlined size="xx-small" />
  ) : (
    <LinkSimpleOutlined size="xx-small" />
  );
};

const getAllEventsTitle = (isDpCurriculum, isMypCurriculum) => {
  if (isDpCurriculum || isMypCurriculum) {
    return "calendar:all_assignments_events_and_project_deadlines";
  }
  return "calendar:all_assignments_and_events";
};

const getAllEventsSubtitle = (isDpCurriculum, isMypCurriculum) => {
  if (isDpCurriculum || isMypCurriculum) {
    return "calendar:sync_all_assignments_events_and_project_deadlines_to_a_single_calendar";
  }
  return "calendar:sync_all_assignments_and_events_to_a_single_calendar";
};

const getExportOptionsWithCurriculum = (isDpCurriculum, isMypCurriculum) => [
  {
    id: "ALL_EVENTS",
    title: getAllEventsTitle(isDpCurriculum, isMypCurriculum),
    subtitle: getAllEventsSubtitle(isDpCurriculum, isMypCurriculum),
    hasLink: true,
    curriculumPermission: "all",
    linkLabel: "calendar:subscribe",
    linkIcon: getLinkIcon(),
  },
  {
    id: "CLASS_STREAM_TASKS",
    title: "calendar:class_assignments",
    subtitle: "calendar:sync_assignments_to_a_dedicated_calendar",
    hasLink: true,
    curriculumPermission: "all",
    linkLabel: "calendar:subscribe",
    linkIcon: getLinkIcon(),
  },
  {
    id: "SCHOOL_EVENTS",
    title: "calendar:school_events_holidays",
    subtitle: "calendar:sync_schoolwide_events_to_a_dedicated_calendar",
    hasLink: true,
    curriculumPermission: "all",
    linkLabel: "calendar:subscribe",
    linkIcon: getLinkIcon(),
  },
  {
    id: "VIDEO_MEETINGS",
    title: "calendar:video_meetings",
    subtitle: "calendar:sync_video_meetings_to_a_dedicated_calendar",
    hasLink: true,
    curriculumPermission: "all",
    linkLabel: "calendar:subscribe",
    linkIcon: getLinkIcon(),
  },
  {
    id: "MYP_DEADLINES",
    title: "calendar:myp_tags_projects",
    subtitle: "calendar:sync_project_deadlines_to_a_dedicated_calednar",
    hasLink: true,
    curriculumPermission: "myp",
    linkLabel: "calendar:subscribe",
    linkIcon: getLinkIcon(),
  },
  {
    id: "DP_DEADLINES",
    title: "calendar:dp_tags_projects",
    subtitle: "calendar:sync_project_deadlines_to_a_dedicated_calednar",
    hasLink: true,
    curriculumPermission: "dp",
    linkLabel: "calendar:subscribe",
    linkIcon: getLinkIcon(),
  },
];

const getOtherExportOptionsWithCurriculum = (
  isDpCurriculum,
  isMypCurriculum
) => [
  {
    id: "ALL_EVENTS",
    title: getAllEventsTitle(isDpCurriculum, isMypCurriculum),
    subtitle: getAllEventsSubtitle(isDpCurriculum, isMypCurriculum),
    hasLink: false,
    curriculumPermission: "all",
    linkLabel: "common:copy_link",
    linkIcon: getLinkIcon(true),
  },
  {
    id: "CLASS_STREAM_TASKS",
    title: "calendar:class_assignments",
    subtitle: "calendar:sync_assignments_to_a_dedicated_calendar",
    hasLink: false,
    curriculumPermission: "all",
    linkLabel: "common:copy_link",
    linkIcon: getLinkIcon(true),
  },
  {
    id: "SCHOOL_EVENTS",
    title: "calendar:school_events_holidays",
    subtitle: "calendar:sync_schoolwide_events_to_a_dedicated_calendar",
    hasLink: false,
    curriculumPermission: "all",
    linkLabel: "common:copy_link",
    linkIcon: getLinkIcon(true),
  },
  {
    id: "VIDEO_MEETINGS",
    title: "calendar:video_meetings",
    subtitle: "calendar:sync_video_meetings_to_a_dedicated_calendar",
    hasLink: false,
    curriculumPermission: "all",
    linkLabel: "common:copy_link",
    linkIcon: getLinkIcon(true),
  },
  {
    id: "MYP_DEADLINES",
    title: "calendar:myp_tags_projects",
    subtitle: "calendar:sync_project_deadlines_to_a_dedicated_calednar",
    hasLink: false,
    curriculumPermission: "myp",
    linkLabel: "common:copy_link",
    linkIcon: getLinkIcon(true),
  },
  {
    id: "DP_DEADLINES",
    title: "calendar:dp_tags_projects",
    subtitle: "calendar:sync_project_deadlines_to_a_dedicated_calednar",
    hasLink: false,
    curriculumPermission: "dp",
    linkLabel: "common:copy_link",
    linkIcon: getLinkIcon(true),
  },
];

const filterOptionList = (options, isDpCurriculum, isMypCurriculum) => {
  return _.filter(options, currOption => {
    if (_.get(currOption, "curriculumPermission") === "all") return true;

    // teacher have curriculum dp
    if (_.get(currOption, "curriculumPermission") === "dp" && isDpCurriculum)
      return true;

    // teacher have curriculum myp
    if (_.get(currOption, "curriculumPermission") === "myp" && isMypCurriculum)
      return true;
  });
};

const getExportOptions = (isDpCurriculum, isMypCurriculum) => {
  const options = filterOptionList(
    getExportOptionsWithCurriculum(isDpCurriculum, isMypCurriculum),
    isDpCurriculum,
    isMypCurriculum
  );

  return options;
};

const getOtherExportOptions = (isDpCurriculum, isMypCurriculum) => {
  const options = filterOptionList(
    getOtherExportOptionsWithCurriculum(isDpCurriculum, isMypCurriculum),
    isDpCurriculum,
    isMypCurriculum
  );

  return options;
};

export const syncModalItemList = (isDpCurriculum, isMypCurriculum) => {
  const isGcalTempfixEnabled = isGcalSubscriptionTempfixEnabled();

  return [
    {
      id: MICROSOFT_CALENDAR,
      calendarLabel: "calendar:microsoft_calendar",
      calendarIcon: <MicrosoftCalendarColored size="xx-small" />,
      exportOptions: getExportOptions(isDpCurriculum, isMypCurriculum),
      title: "calendar:sync_microsoft_calendar_question",
      steps: [
        {
          actions: [
            "calendar:login_microsoft_on_same_browser",
            "calendar:click_on_subscribe_button_text_v2",
          ],
        },
      ],
    },
    {
      id: GOOGLE_CALENDAR,
      calendarLabel: "calendar:google_calendar",
      calendarIcon: <GoogleCalendarColored size="xx-small" />,
      exportOptions: isGcalTempfixEnabled
        ? getOtherExportOptions(isDpCurriculum, isMypCurriculum)
        : getExportOptions(isDpCurriculum, isMypCurriculum),
      title: "calendar:sync_your_google_cal_question",
      steps: isGcalTempfixEnabled
        ? [
            {
              actions: [
                "calendar:google_calendar_left_sidebar",
                "calendar:click_on_from_url_option",
                "calendar:paste_the_link_google_calendar",
                "calendar:click_on_add_calendar_button",
              ],
            },
          ]
        : [
            {
              actions: [
                "calendar:login_google_account_on_same_browser",
                "calendar:click_on_subscribe_button_text_v2",
              ],
            },
          ],
      quickAddSection: {
        title: "calendar:google_quick_add_title",
        description: "calendar:google_quick_add_description",
      },
    },
    {
      id: "others",
      calendarLabel: "common:others",
      calendarIcon: <CalendarScheduleWeekOutlined size="xx-small" />,
      exportOptions: getOtherExportOptions(isDpCurriculum, isMypCurriculum),
      title: "calendar:sync_your_calendar_question",
      steps: [
        {
          actions: [
            "calendar:subscribe_to_calendar",
            "calendar:paste_the_link_v2",
            "calendar:customize_settings",
            "calendar:save_your_changes",
          ],
        },
      ],
    },
  ];
};

/**
 * @param currentCurriculumProgramTypes is an array
 */
export const getSyncModalItemList = currentCurriculumProgramTypes => {
  const isDpCurriculum = _.some(currentCurriculumProgramTypes, [
    "type",
    "IB_DP",
  ]);
  const isMypCurriculum = _.some(currentCurriculumProgramTypes, [
    "type",
    "IB_MYP",
  ]);

  return syncModalItemList(isDpCurriculum, isMypCurriculum);
};

export const getSelectedSyncObject = (
  selectedSyncCalendarLabel,
  currentCurriculumProgramType
) => {
  return _.find(
    getSyncModalItemList(currentCurriculumProgramType),
    currSyncItemObj =>
      _.get(currSyncItemObj, "id", "") === selectedSyncCalendarLabel
  );
};

// Auto-generated exports
export {
  getLinkIcon,
  filterOptionList,
  getExportOptions,
  getOtherExportOptions,
};

export const setPopoverModalContentLocation = props => {
  const { childRect, popoverRect, parentRect, boundaryInset = 20 } = props;

  const MODAL_WIDTH = popoverRect.width;
  const MODAL_HEIGHT = popoverRect.height;
  const MARGIN = 12;
  const PARENT_VISIBILITY_MARGIN = 180; // Minimum parent visibility when popover is below or above

  // Get viewport dimensions
  const viewportWidth = parentRect.width;
  const viewportHeight = parentRect.height;

  // Calculate available space in each direction
  const spaceRight = viewportWidth - childRect.right - boundaryInset;
  const spaceLeft = childRect.left - boundaryInset;
  const spaceBottom = viewportHeight - childRect.bottom - boundaryInset;
  const spaceTop = childRect.top - boundaryInset;

  let position = "bottom-right"; // default position
  let top = childRect.bottom + MARGIN;
  let left = childRect.left;

  // Determine best horizontal position
  if (spaceRight >= MODAL_WIDTH) {
    // Position to the right of the element
    left = childRect.right + MARGIN;
    position = "right";
  } else if (spaceLeft >= MODAL_WIDTH) {
    // Position to the left of the element
    left = childRect.left - MODAL_WIDTH - MARGIN;
    position = "left";
  } else {
    // Position below/above the element, determine horizontal alignment
    if (childRect.left + MODAL_WIDTH <= viewportWidth - boundaryInset) {
      // Align to the left edge of the element
      left = childRect.left;
    } else if (childRect.right - MODAL_WIDTH >= boundaryInset) {
      // Align to the right edge of the element
      left = childRect.right - MODAL_WIDTH;
    } else {
      // Center horizontally in available space
      left = Math.max(
        boundaryInset,
        Math.min(
          viewportWidth - MODAL_WIDTH - boundaryInset,
          childRect.left + (childRect.width - MODAL_WIDTH) / 2
        )
      );
    }
  }

  // Determine vertical position
  if (position === "right" || position === "left") {
    // For side positioning, try to vertically center on the element
    top = childRect.top + (childRect.height - MODAL_HEIGHT) / 2;

    // Ensure modal stays within viewport bounds
    if (top < boundaryInset) {
      top = boundaryInset;
    } else if (top + MODAL_HEIGHT > viewportHeight - boundaryInset) {
      top = viewportHeight - MODAL_HEIGHT - boundaryInset;
    }
  } else {
    // For top/bottom positioning
    if (spaceBottom >= MODAL_HEIGHT) {
      // Position below the element
      top = childRect.bottom + MARGIN;
      position = position === "left" ? "bottom-left" : "bottom-right";

      // Apply parent visibility margin when positioning below
      left = Math.max(left, childRect.left + PARENT_VISIBILITY_MARGIN);

      // Ensure the adjusted left position doesn't push modal out of viewport
      left = Math.min(left, viewportWidth - MODAL_WIDTH - boundaryInset);
    } else if (spaceTop >= MODAL_HEIGHT) {
      // Position above the element
      top = childRect.top - MODAL_HEIGHT - MARGIN;
      position = position === "left" ? "top-left" : "top-right";

      // Apply parent visibility margin when positioning above
      left = Math.max(left, childRect.left + PARENT_VISIBILITY_MARGIN);

      // Ensure the adjusted left position doesn't push modal out of viewport
      left = Math.min(left, viewportWidth - MODAL_WIDTH - boundaryInset);
    } else {
      // Not enough space above or below, position where there's more space
      if (spaceBottom > spaceTop) {
        top = childRect.bottom + MARGIN;

        // Apply parent visibility margin when positioning below
        left = Math.max(left, childRect.left + PARENT_VISIBILITY_MARGIN);

        // Adjust height if needed
        if (top + MODAL_HEIGHT > viewportHeight - boundaryInset) {
          top = viewportHeight - MODAL_HEIGHT - boundaryInset;
        }
      } else {
        top = Math.max(boundaryInset, childRect.top - MODAL_HEIGHT - MARGIN);

        // Apply parent visibility margin when positioning above
        left = Math.max(left, childRect.left + PARENT_VISIBILITY_MARGIN);

        // Ensure the adjusted left position doesn't push modal out of viewport
        left = Math.min(left, viewportWidth - MODAL_WIDTH - boundaryInset);
      }
    }
  }

  // Final boundary checks to ensure modal stays within viewport
  left = Math.max(
    boundaryInset,
    Math.min(left, viewportWidth - MODAL_WIDTH - boundaryInset)
  );

  top = Math.max(
    boundaryInset,
    Math.min(top, viewportHeight - MODAL_HEIGHT - boundaryInset)
  );

  return {
    top,
    left,
    position, // Optional: return the calculated position for debugging
  };
};
